"use client";
import classNames from "classnames";
import IconClose from "@/components/Icon/IconClose.svg";
import Image from "next/image";
import Link from "next/link";
import { GIFTCARD_IMAGES, GIFT_CARD_NAME, GIFT_CARD_SLUG } from "@/constants";
import Money from "@/components/Money";
import { useCart, useCheckout } from "@/lib/saleor/reactSaleor";
import { calcItemDiscount, calcCartDiscount, calcTotalItemsDiscount } from "@/lib/voucherify/utils";
import IncentivePromotions from "@/components/IncentivePromotions";
import { useRedeemablePromotions } from "@/lib/voucherify/useRedeemablePromotions";

export default function AddToCartDialog({ open, onChange }) {
  const { items, totalPrice, subtotalPrice } = useCart();
  const { promotions: voucherifies, loading: isLoadingVoucherifies } = useRedeemablePromotions([]);
  const itemsDiscount = calcTotalItemsDiscount(voucherifies);
  const cartDiscount = calcCartDiscount(voucherifies);

  return (
    <>
      <div className="hidden md:block">
        <div className={`dialog__slide ${open ? "show" : ""}`}>
          <div className="dialog__content right">
            <div className="w-[489px] h-screen bg-white overflow-y-auto">
              <div className="px-[48px]">
                {/* Show promotion banner */}
                {/* <IncentivePromotions /> */}
              </div>
              <div className="flex gap-[64px] pt-[60px] px-[48px] pb-[14px]">
                <div class="font-rajdhani font-bold text-[21px] leading-[26px] ">
                  SUCCESS. YOU HAVE ADDED THIS ITEM TO YOUR CART
                </div>
                <Image
                  src={IconClose.src}
                  alt="IconClose"
                  width={16}
                  height={16}
                  className="cursor-pointer h-fit"
                  onClick={() => onChange(false)}
                />
              </div>
              <div className="px-[48px] py-4">
                {items &&
                  items.length > 0 &&
                  items.map((item, index) => {
                    const name = item.variant?.product?.name;
                    const size = item.variant?.attributes?.find(attr => attr.attribute.name === "Sizes");
                    const colour = item.variant?.attributes?.find(attr => attr.attribute.name === "Colour");
                    const price = item.variant?.pricing?.price?.gross?.amount;
                    const alt = item.variant?.product?.thumbnail?.alt;
                    var image = item.variant?.product?.thumbnail?.url;
                    var renderSlug = item.variant?.product?.slug;
                    if (name === GIFT_CARD_NAME) {
                      renderSlug = GIFT_CARD_SLUG;
                      image = GIFTCARD_IMAGES.imageGiftCard;
                    }
                    const itemDiscount = calcItemDiscount(voucherifies, item);
                    return (
                      <div
                        key={`product-${index}`}
                        className={classNames("flex mb-4 pt-[30px]", {
                          "border-t-[1px] border-grey-200": index !== 0,
                        })}
                      >
                        <div className="w-1/2">
                          <Link href={`/products/${renderSlug}`}>
                            <Image src={image} width="180" height="180" alt={alt} />
                          </Link>
                        </div>
                        <div className="w-1/2 px-4">
                          <Link
                            href={`/products/${renderSlug}`}
                            className="text-[21px] leading-6 font-[500] font-rajdhani"
                          >
                            {name}
                          </Link>
                          <p className="text-[21px] font-rajdhani font-bold mb-[6px] mt-[10px]">
                            <strong>
                              A${String(price).split(".")[0]}
                              <sup className="font-bold">{String(price).split(".")[1]}</sup>
                            </strong>
                            <br />
                            {itemDiscount > 0 && (
                              <span className="font-bold text-green">
                                <Money amount={-Math.abs(itemDiscount)} />
                              </span>
                            )}
                          </p>
                          {size && <p className="text-base leading-5">Size: {size.values[0].name}</p>}
                          {colour && <p className="text-base leading-5">Colour: {colour.values[0].name}</p>}
                          <p className="text-base leading-5">Qty: {item.quantity}</p>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className="px-[48px] pb-[48px]">
                <div className="my-4 py-[26px] px-6 bg-black">
                  <div className="flex mb-4">
                    <div className="w-1/2">
                      <p className=" text-grey">Sub Total</p>
                    </div>
                    <div className="w-1/2 ">
                      <p className="text-grey text-end font-bold">
                        A<Money amount={subtotalPrice?.gross?.amount} />
                      </p>
                    </div>
                  </div>
                  <div className="flex mb-4">
                    {isLoadingVoucherifies ? (
                      <div className="w-full h-6 bg-grey-400 animate-pulse"></div>
                    ) : !!cartDiscount ? (
                      <>
                        <div className="w-1/2">
                          <p className="text-grey">Discount</p>
                        </div>
                        <div className="w-1/2 ">
                          <p className="text-end font-bold text-green">
                            <Money amount={-Math.abs(cartDiscount)} />
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="w-full min-h-6"></div>
                    )}
                  </div>
                  <div className="flex">
                    <div className="w-1/2">
                      <p className="font-rajdhani font-bold text-[21px] text-white">TOTAL</p>
                    </div>
                    <div className="w-1/2 ">
                      {isLoadingVoucherifies ? (
                        <div className="w-full h-8 bg-grey-400 animate-pulse ml-auto"></div>
                      ) : (
                        <p className="font-rajdhani font-bold text-[21px] text-white text-end">
                          A<Money amount={subtotalPrice?.gross?.amount - itemsDiscount - cartDiscount} />
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex">
                  <div className="w-1/2 pr-4">
                    <button
                      className="w-full h-[48px] text-center font-rajdhani font-bold text-sm lg:text-lg border-[1px] uppercase"
                      onClick={() => onChange(false)}
                    >
                      Continue shopping
                    </button>
                  </div>
                  <div className="w-1/2 pl-4">
                    <Link href="/checkout">
                      <button className="w-full h-[48px] text-center font-rajdhani font-bold text-sm lg:text-lg bg-gold uppercase">
                        Checkout
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="dialog__overlay" onClick={() => onChange(false)}></div>
        </div>
      </div>
      <div className="block md:hidden">
        <div className={`dialog__slide ${open ? "show" : ""}`}>
          <div className="dialog__content center">
            <div className="w-screen h-fit  px-4">
              <div className="w-full h-full bg-white">
                <div className="flex gap-[64px] pt-9 px-7">
                  <div class="font-rajdhani text-[21px] leading-[26px] text-center">
                    SUCCESS. YOU HAVE ADDED THIS ITEM TO YOUR CART
                  </div>
                </div>

                <div className="flex flex-col gap-2 px-8 pt-8 pb-4">
                  <Link href="/checkout">
                    <button className="w-full h-[48px] text-center font-rajdhani font-[600] text-sm lg:text-lg bg-gold uppercase">
                      Checkout
                    </button>
                  </Link>
                  <button
                    className="w-full h-[48px] text-center font-rajdhani font-[600] text-sm lg:text-lg border-[1px] uppercase rounded-md"
                    onClick={() => onChange(false)}
                  >
                    Continue shopping
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="dialog__overlay" onClick={() => onChange(false)}></div>
        </div>
      </div>
    </>
  );
}
